import {
  AI_BUDGET_CATEGORIES,
  AIBudgetCategory,
  AIBudgetStatus,
} from '@/hooks/useAIBudget';

export type CategoryAllocations = Record<AIBudgetCategory, number>;
export type CategoryPercentages = Record<AIBudgetCategory, number>;

export type CategoryBudgetPresetKey = 'balanced' | 'hannibal' | 'equal';

export interface CategoryBudgetPreset {
  key: CategoryBudgetPresetKey;
  label: string;
  description: string;
  weights: CategoryPercentages;
}

export const CATEGORY_BUDGET_PRESETS: CategoryBudgetPreset[] = [
  {
    key: 'balanced',
    label: 'Balanced',
    description: '30% / 25% / 20% / 15% / 10%',
    weights: {
      hunts: 30,
      marcus: 25,
      constantine: 20,
      cato: 15,
      'h1-triage': 10,
    },
  },
  {
    key: 'hannibal',
    label: 'Hannibal focused',
    description: '40% / 20% / 15% / 15% / 10%',
    weights: {
      hunts: 40,
      marcus: 20,
      constantine: 15,
      cato: 15,
      'h1-triage': 10,
    },
  },
  {
    key: 'equal',
    label: 'Equal',
    description: '20% / 20% / 20% / 20% / 20%',
    weights: {
      hunts: 20,
      marcus: 20,
      constantine: 20,
      cato: 20,
      'h1-triage': 20,
    },
  },
];

const toCents = (value: number) => Math.round(value * 100);
const fromCents = (value: number) => value / 100;
// 0 is a valid allocation: it disables the category (the backend hard-blocks
// spend on a 0-limit budget), so no minimum floor is applied.
const MINIMUM_ALLOCATION_CENTS = 0;

const categoryKeys: AIBudgetCategory[] = AI_BUDGET_CATEGORIES.map(c => c.key);

const distributeByWeight = (
  totalUnits: number,
  weights: CategoryPercentages,
  minimumUnits = 0
): CategoryPercentages => {
  const distributed = Object.fromEntries(
    categoryKeys.map(key => [key, minimumUnits])
  ) as CategoryPercentages;
  const remainingUnits = totalUnits - minimumUnits * categoryKeys.length;
  const weightTotal = Object.values(weights).reduce(
    (total, weight) => total + Math.max(0, weight),
    0
  );
  if (remainingUnits < 0 || weightTotal <= 0) return distributed;

  const weighted = categoryKeys.map(key => {
    const exact = (Math.max(0, weights[key]) * remainingUnits) / weightTotal;
    const units = Math.floor(exact);
    return { key, units, remainder: exact - units };
  });
  let remainder =
    remainingUnits - weighted.reduce((total, item) => total + item.units, 0);
  for (const item of [...weighted].sort(
    (left, right) => right.remainder - left.remainder
  )) {
    if (remainder === 0) break;
    item.units++;
    remainder--;
  }
  for (const { key, units } of weighted) distributed[key] += units;
  return distributed;
};

const centsToAllocations = (cents: CategoryPercentages): CategoryAllocations =>
  Object.fromEntries(
    Object.entries(cents).map(([key, value]) => [key, fromCents(value)])
  ) as CategoryAllocations;

export const buildPresetAllocations = (
  overallLimit: number,
  preset: CategoryBudgetPreset
): CategoryAllocations =>
  centsToAllocations(
    distributeByWeight(
      toCents(overallLimit),
      preset.weights,
      MINIMUM_ALLOCATION_CENTS
    )
  );

export const buildInitialAllocations = (
  overallLimit: number,
  categories?: Partial<Record<AIBudgetCategory, AIBudgetStatus>>
): CategoryAllocations => {
  const balanced = buildPresetAllocations(
    overallLimit,
    CATEGORY_BUDGET_PRESETS[0]
  );
  const configured = AI_BUDGET_CATEGORIES.filter(
    ({ key }) => categories?.[key]?.set
  );
  if (configured.length === 0) return balanced;

  const initial = Object.fromEntries(
    AI_BUDGET_CATEGORIES.map(({ key }) => [
      key,
      categories?.[key]?.set
        ? categories[key].max_limit
        : fromCents(MINIMUM_ALLOCATION_CENTS),
    ])
  ) as CategoryAllocations;
  const difference =
    toCents(overallLimit) -
    Object.values(initial).reduce((total, value) => total + toCents(value), 0);

  if (difference < 0) return balanced;
  initial.marcus = fromCents(toCents(initial.marcus) + difference);
  return initial;
};

export const allocationsToPercentages = (
  overallLimit: number,
  allocations: CategoryAllocations
): CategoryPercentages => {
  if (overallLimit <= 0)
    return Object.fromEntries(
      categoryKeys.map(k => [k, 0])
    ) as CategoryPercentages;
  return distributeByWeight(
    100,
    Object.fromEntries(
      categoryKeys.map(key => [key, toCents(allocations[key])])
    ) as CategoryPercentages,
    MINIMUM_ALLOCATION_CENTS
  );
};

export const buildInitialPercentages = (
  overallLimit: number,
  categories?: Partial<Record<AIBudgetCategory, AIBudgetStatus>>
): CategoryPercentages =>
  allocationsToPercentages(
    overallLimit,
    buildInitialAllocations(overallLimit, categories)
  );

export const buildPresetPercentages = (
  _overallLimit: number,
  preset: CategoryBudgetPreset
): CategoryPercentages =>
  distributeByWeight(100, preset.weights, MINIMUM_ALLOCATION_CENTS);

export const percentagesToAllocations = (
  overallLimit: number,
  percentages: CategoryPercentages
): CategoryAllocations =>
  centsToAllocations(
    distributeByWeight(
      toCents(overallLimit),
      percentages,
      MINIMUM_ALLOCATION_CENTS
    )
  );

export const adjustCategoryPercentage = (
  percentages: CategoryPercentages,
  category: AIBudgetCategory,
  direction: 1 | -1
): CategoryPercentages => {
  const others = categoryKeys.filter(k => k !== category);
  const step = direction * others.length;
  const adjusted = { ...percentages };
  adjusted[category] += step;
  for (const other of others) adjusted[other] -= direction;
  return Object.values(adjusted).every(value => value >= 0 && value <= 100)
    ? adjusted
    : percentages;
};

export const validateCategoryPercentages = (
  _overallLimit: number,
  percentages: CategoryPercentages
): string | null => {
  for (const { key, label } of AI_BUDGET_CATEGORIES) {
    const value = percentages[key];
    if (!Number.isFinite(value) || !Number.isInteger(value) || value < 0) {
      return `Enter a whole percentage of 0% or more for ${label}.`;
    }
  }

  const allocatedPercentage = Object.values(percentages).reduce(
    (total, value) => total + value,
    0
  );
  const difference = 100 - allocatedPercentage;
  if (difference > 0) return `${difference}% remains unallocated.`;
  if (difference < 0) return `Reduce percentages by ${-difference}%.`;
  return null;
};
